import { PixelBuffer, crossFade, hexOf, rgb } from "./pixel_raster"
import { GLYPHS } from "./pixel_wordmark_glyphs"

/**
 * The "samsara" wordmark as pixel art: four hand-drawn letters, shared by the HTML loading screen
 * (loading_screen.ts paints them into canvases before the game has loaded) and the login screen
 * (project_logo.ts), so the letters that fly from one to the other are the same pixels.
 *
 * Pure: no DOM, no pixi.
 */

/** The letters' x-height on the loading screen and in the login screen's wordmark, in art pixels. */
export const LOADING_TITLE_H = 12
export const TITLE_H = 20

/** Between one letter and the next, for an x-height: the type wordmark's wide tracking. */
export const trackingFor = (h: number): number => Math.round(h * 0.55)

/**
 * The glyph's coverage at x-height `h`, one string per row, each digit a pixel's coverage in tenths.
 * Baked from Jost itself (packages/tools/src/wordmark/bake.mjs) at every size the wordmark grows
 * through, so the pixel letters are the type wordmark's letters, not an imitation of them.
 */
export const glyphRows = (char: string, h: number): string[] => {
    const rows = GLYPHS[h]?.[char]
    if (!rows) {
        throw new Error(`No pixel glyph for "${char}" at x-height ${h}`)
    }
    return rows
}

/**
 * How the letters are drawn from their coverage:
 *  - soft: partial coverage becomes translucent anti-alias steps. The look: the user tried crisp and
 *    found it worse than this.
 *  - crisp: every pixel is ink or empty, the colour varying inside the strokes instead (banded cream
 *    to gold, a lit top edge, a warmer underside). `?title=crisp` shows it, to compare.
 */
export type TitleStyle = "crisp" | "soft"
export const TITLE_STYLE: TitleStyle =
    typeof location !== "undefined" && /[?&]title=crisp\b/.test(location.search) ? "crisp" : "soft"

/** Cream at the top of the letters warming toward gold at their feet: Jost's own colour, banded. */
const FILL = [0xfff4e2, 0xfcecd6, 0xf8dfbd, 0xf1cd9a].map(h => rgb(h))
/** Crisp only: the top edge of a stroke catches the light, its underside runs a step warmer. */
const LIT_EDGE = rgb(0xfffbf2)
const UNDERSIDE = rgb(0xe6b878)
/** Soft only: alpha by coverage. Translucent, so the letters sit as softly on any background. */
const softAlpha = (tenths: number): number => (tenths >= 7 ? 255 : tenths >= 4 ? 170 : tenths >= 2 ? 85 : 0)

/**
 * Which pixels are ink when every pixel must be ink or empty: those at least half covered, plus any
 * a third covered that joins two separate parts of the stroke -- a light weight's thin curves would
 * otherwise break where they run between two pixels.
 */
const inkOf = (rows: string[]): Uint8Array => {
    const w = rows[0].length
    const h = rows.length
    const cov = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < h ? rows[y].charCodeAt(x) - 48 : 0)
    const ink = new Uint8Array(w * h)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) ink[y * w + x] = cov(x, y) >= 5 ? 1 : 0
    const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < h ? ink[y * w + x] : 0)
    const ring: [number, number][] = [[0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1]]
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            if (ink[y * w + x] || cov(x, y) < 3) continue
            // Pieces of ink around the pixel: count the gaps between runs going round its 8 neighbours.
            let pieces = 0
            for (let k = 0; k < 8; k++) {
                const [ax, ay] = ring[k]
                const [bx, by] = ring[(k + 1) % 8]
                if (!at(x + ax, y + ay) && at(x + bx, y + by)) pieces++
            }
            if (pieces >= 2) ink[y * w + x] = 1
        }
    }
    return ink
}

/** One letter at one size: its advance, the lettering with its outline, and the glow behind it. */
export interface TitleLetter {
    char: string
    /** x-height, in art pixels. */
    h: number
    /** The glyph's width: what the letters are spaced by. */
    advance: number
    /** The letter: `advance` wide, `h + 2` tall (a row of overshoot above and below). */
    body: PixelBuffer
    /** White, its alpha stepping down away from the glyph; GLOW_PAD wider than the body each side. */
    glow: PixelBuffer
}

const GLOW_PAD = 4

const letters = new Map<string, TitleLetter>()

export const titleLetter = (char: string, h: number = TITLE_H, style: TitleStyle = TITLE_STYLE): TitleLetter => {
    const key = `${char}:${h}:${style}`
    const cached = letters.get(key)
    if (cached) {
        return cached
    }
    const rows = glyphRows(char, h)
    const w = rows[0].length
    const ink = inkOf(rows)
    const coverage = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < rows.length ? rows[y].charCodeAt(x) - 48 : 0)
    const inkAt = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < rows.length && ink[y * w + x] === 1
    /** What the glow spreads from: the ink, or (soft) everything well covered. */
    const solid = (x: number, y: number) => (style === "crisp" ? inkAt(x, y) : coverage(x, y) >= 4)

    // The body, in the fill colour of its row. No outline -- the type wordmark had none, and a dark
    // rim makes every step of a curve stand out.
    const body = new PixelBuffer(w, rows.length)
    for (let y = 0; y < rows.length; y++) {
        const fill = FILL[Math.max(0, Math.min(FILL.length - 1, Math.floor(((y - 1) / h) * FILL.length)))]
        for (let x = 0; x < w; x++) {
            if (style === "soft") {
                const alpha = softAlpha(coverage(x, y))
                if (alpha) body.set(x, y, rgb(hexOf(fill), alpha))
            } else if (inkAt(x, y)) {
                body.set(x, y, !inkAt(x, y - 1) ? LIT_EDGE : !inkAt(x, y + 1) ? UNDERSIDE : fill)
            }
        }
    }

    // The glow: distance from the glyph's solid pixels, in steps, never a smooth falloff.
    const pad = GLOW_PAD
    const glow = new PixelBuffer(w + pad * 2, rows.length + pad * 2)
    for (let y = 0; y < glow.h; y++) {
        for (let x = 0; x < glow.w; x++) {
            let d = Infinity
            for (let j = -pad - 1; j <= pad + 1 && d > 0; j++) {
                for (let i = -pad - 1; i <= pad + 1; i++) {
                    if (solid(x - pad + i, y - pad + j)) d = Math.min(d, Math.hypot(i, j))
                }
            }
            const alpha = d <= 1.5 ? 150 : d <= 3 ? 80 : d <= pad + 0.5 ? 34 : 0
            if (alpha) glow.set(x, y, rgb(0xffffff, alpha))
        }
    }

    const letter = { char, h, advance: w, body, glow }
    letters.set(key, letter)
    return letter
}

/** Buffers a letter grows inside: the size of its body and glow at the title's x-height. */
export const growBuffers = (char: string) => {
    const big = titleLetter(char, TITLE_H)
    return { body: new PixelBuffer(big.body.w, big.body.h), glow: new PixelBuffer(big.glow.w, big.glow.h) }
}

/**
 * A letter part way through growing, at a fractional x-height: the whole sizes either side of it,
 * the larger cross-faded in over the smaller, each centred in `growBuffers`. Every pixel is the same
 * size at every step; only which pixels are lit changes.
 *
 * Returns where the glyph's centre is relative to the buffers' centres, in art pixels, so the caller
 * can hold it still: an odd and an even size can't both be centred exactly.
 */
export const paintGrowingLetter = (char: string, h: number, body: PixelBuffer, glow: PixelBuffer): { dx: number; dy: number } => {
    const h0 = Math.max(LOADING_TITLE_H, Math.min(TITLE_H, Math.floor(h)))
    const h1 = Math.min(TITLE_H, h0 + 1)
    const w = h1 === h0 ? 0 : Math.max(0, Math.min(1, h - h0))
    const place = (size: number, into: PixelBuffer, layer: "body" | "glow") => {
        const art = titleLetter(char, size)[layer]
        const out = new PixelBuffer(into.w, into.h)
        const ox = Math.floor((into.w - art.w) / 2)
        const oy = Math.floor((into.h - art.h) / 2)
        out.draw(art, ox, oy)
        return { out, dx: ox + art.w / 2 - into.w / 2, dy: oy + art.h / 2 - into.h / 2 }
    }
    const a = place(h0, body, "body")
    const b = place(h1, body, "body")
    body.clear()
    crossFade(body, a.out, b.out, w)
    glow.clear()
    crossFade(glow, place(h0, glow, "glow").out, place(h1, glow, "glow").out, w)
    return { dx: a.dx + (b.dx - a.dx) * w, dy: a.dy + (b.dy - a.dy) * w }
}
